import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { ServiceCaseDetailDto } from '../../../../../../core/models/customer-service/customer-service-case.model';
import { CustomerServiceCaseMapper } from '../../../../../../core/mappers/customer-service/mapper-customer-service-case.service';
import { ApiCustomerServiceCaseService } from '../../../../../../core/services/api/customer-service/api-customer-service-case.service';
import { ApiSystemSettingsService } from '../../../../../../core/services/api/system-settings/api-system-settings.service';
import { MenuPermissionService } from '../../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-customer-service-case-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-customer-service-case-detail.component.html',
  styleUrl: './modal-customer-service-case-detail.component.css'
})
export class ModalCustomerServiceCaseDetailComponent implements OnInit, OnDestroy {
  @Input() caseId!: number;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  // ================= State Variables =================
  isLoading = true;
  isVisible = false;
  caseDetail!: ServiceCaseDetailDto;
  private timeTickInterval: any;

  // --- Sub-Dialog Control ---
  showAssignDialog = false;
  selectedStaffId: number | null = null;
  staffList: Array<{ id: number; name: string; username: string }> = [];

  showCancelDialog = false;
  cancelReason = '';

  showStatusDialog = false;
  targetStatus = '';
  statusMessage = '';
  resolutionSummary = '';
  isSubmittingStatus = false;
  previewImageUrl: string | null = null;

  // --- Add Log reply form ---
  newLogMessage = '';
  newLogType: 'staff_reply' | 'internal_note' = 'staff_reply';
  isSavingLog = false;

  private readonly apiCase = inject(ApiCustomerServiceCaseService);
  private readonly mapper = inject(CustomerServiceCaseMapper);
  private readonly apiSystem = inject(ApiSystemSettingsService);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  get currentUrl(): string {
    return '/customer-service/customer-service-case';
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.currentUrl);
  }

  // ================= Normal Functions =================

  ngOnInit(): void {
    setTimeout(() => {
      this.isVisible = true;
    }, 50);

    this.loadCaseDetail();
    this.loadStaffList();

    // Start timer to update ticks every second for live countdown
    this.timeTickInterval = setInterval(() => {}, 1000);
  }

  ngOnDestroy(): void {
    if (this.timeTickInterval) {
      clearInterval(this.timeTickInterval);
    }
  }

  onClose(): void {
    if (this.previewImageUrl) {
      this.closeImagePreview();
      return;
    }
    this.isVisible = false;
    setTimeout(() => {
      this.close.emit();
    }, 200);
  }

  /**
   * Safe check values and trigger output refresh
   */
  notifyRefresh(): void {
    this.saveSuccess.emit();
  }

  /**
   * Helper mapping labels
   */
  getPriorityLabel(priority: string): string {
    return this.mapper.translatePriority(priority);
  }

  getPriorityColorClass(priority: string): string {
    return this.mapper.getPriorityColorClass(priority);
  }

  getChannelLabel(channel: string): string {
    return this.mapper.translateChannel(channel);
  }

  getStatusLabel(status: string): string {
    return this.mapper.translateStatus(status);
  }

  getStatusColorClass(status: string): string {
    return this.mapper.getStatusColorClass(status);
  }

  getLogActionIcon(actionType: string): string {
    switch (actionType) {
      case 'system': return 'settings';
      case 'status_change': return 'swap_horiz';
      case 'staff_reply': return 'forum';
      case 'internal_note': return 'notes';
      case 'assign': return 'person_add';
      case 'case_cancelled': return 'cancel';
      case 'attachment': return 'attach_file';
      case 'resolution_executed': return 'verified';
      case 'customer_confirmed': return 'done_all';
      default: return 'circle';
    }
  }

  openAssignDialog(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedStaffId = this.caseDetail.assignedUserId ?? 0;
    this.showAssignDialog = true;
  }

  openCancelDialog(): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.cancelReason = '';
    this.showCancelDialog = true;
  }

  openStatusDialog(newStatus: string): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.targetStatus = newStatus;
    this.statusMessage = '';
    this.resolutionSummary = '';
    this.showStatusDialog = true;
  }

  openImagePreview(url: string): void {
    this.previewImageUrl = url;
  }

  closeImagePreview(): void {
    this.previewImageUrl = null;
  }

  /**
   * Calculates elapsed or resolution time
   * เทียบแบบคำนวณชั่วโมงให้กับเวลาปัจจุบัน
   */
  getCaseDurationInfo(caseDetail: ServiceCaseDetailDto | undefined): { label: string; duration: string; isCompleted: boolean } {
    if (!caseDetail || !caseDetail.createDate) {
      return { label: 'ระยะเวลา', duration: '-', isCompleted: false };
    }

    const created = new Date(caseDetail.createDate);
    if (isNaN(created.getTime())) {
      return { label: 'ระยะเวลา', duration: '-', isCompleted: false };
    }

    let endTime = new Date();
    let isCompleted = false;
    let label = 'ระยะเวลาที่เปิดเรื่อง (ถึงปัจจุบัน)';

    if (caseDetail.status === 'closed') {
      isCompleted = true;
      label = 'ระยะเวลาดำเนินการจนปิดเคส';
      if (caseDetail.closedAt) {
        endTime = new Date(caseDetail.closedAt);
      } else if (caseDetail.resolvedAt) {
        endTime = new Date(caseDetail.resolvedAt);
      }
    } else if (caseDetail.status === 'cancelled') {
      isCompleted = true;
      label = 'ระยะเวลาดำเนินการก่อนยกเลิก';
      if (caseDetail.cancelledAt) {
        endTime = new Date(caseDetail.cancelledAt);
      }
    }

    const diffMs = endTime.getTime() - created.getTime();
    if (diffMs < 0) {
      return { label, duration: 'เพิ่งเปิดเคส', isCompleted };
    }

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffSeconds < 60) {
      return { label, duration: `${diffSeconds} วินาที`, isCompleted };
    }

    const remainingSeconds = diffSeconds % 60;
    const remainingMins = diffMins % 60;
    let durationStr = '';

    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      durationStr = `${diffDays} วัน ${remainingHours} ชั่วโมง ${remainingMins} นาที ${remainingSeconds} วินาที`;
    } else if (diffHours > 0) {
      durationStr = `${diffHours} ชั่วโมง ${remainingMins} นาที ${remainingSeconds} วินาที`;
    } else {
      durationStr = `${diffMins} นาที ${remainingSeconds} วินาที`;
    }

    return { label, duration: durationStr, isCompleted };
  }

  // ================= API Functions =================

  loadCaseDetail(): void {
    this.isLoading = true;
    this.apiCase.getServiceCaseDetail(this.caseId).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.caseDetail = this.mapper.mapCaseDetail(res);
      },
      error: (err) => {
        this.isLoading = false;
        this.toast.error('ไม่สามารถโหลดรายละเอียดของเคสปัญหาได้');
        console.error(err);
        this.onClose();
      }
    });
  }

  loadStaffList(): void {
    this.apiSystem.getAccessUsersForTable(1, 200).subscribe({
      next: (res) => {
        const list = res.data || [];
        this.staffList = list.map((u: any) => ({
          id: u.id ?? u.Id,
          username: u.userName ?? u.UserName ?? '',
          name: u.fullName ?? u.FullName ?? u.userName ?? u.UserName ?? '-'
        }));
      },
      error: () => (this.staffList = [])
    });
  }

  submitStaffReplyLog(): void {
    if (!this.newLogMessage.trim()) {
      this.toast.error('กรุณากรอกข้อความตอบกลับ');
      return;
    }

    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.currentUrl) ?? 0;

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = {
      userId,
      menuId,
      serviceCaseId: this.caseId,
      actionType: this.newLogType,
      message: this.newLogMessage.trim(),
      isCustomerVisible: this.newLogType === 'staff_reply'
    };

    this.isSavingLog = true;
    this.apiCase.addServiceCaseLog(payload).subscribe({
      next: (res) => {
        this.isSavingLog = false;
        if (res.isSuccess) {
          this.toast.success(res.message || 'บันทึกข้อความสำเร็จ');
          this.newLogMessage = '';
          this.loadCaseDetail();
          this.notifyRefresh();
        } else {
          this.toast.error(res.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSavingLog = false;
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการบันทึกข้อความ');
      }
    });
  }

  submitAssignment(): void {
    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.currentUrl) ?? 0;

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = {
      userId,
      menuId,
      serviceCaseId: this.caseId,
      assignedUserId: this.selectedStaffId || null
    };

    this.apiCase.assignServiceCase(payload).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.toast.success(res.message || 'มอบหมายสำเร็จ');
          this.showAssignDialog = false;
          this.loadCaseDetail();
          this.notifyRefresh();
        } else {
          this.toast.error(res.message || 'มอบหมายไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการมอบหมายเจ้าหน้าที่');
      }
    });
  }

  submitCancellation(): void {
    if (!this.cancelReason.trim()) {
      this.toast.error('กรุณาระบุเหตุผลในการยกเลิกเคส');
      return;
    }

    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.currentUrl) ?? 0;

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = {
      userId,
      menuId,
      serviceCaseId: this.caseId,
      cancelReason: this.cancelReason.trim()
    };

    this.apiCase.cancelServiceCase(payload).subscribe({
      next: (res) => {
        if (res.isSuccess) {
          this.toast.success(res.message || 'ยกเลิกเคสปัญหานี้สำเร็จ');
          this.showCancelDialog = false;
          this.loadCaseDetail();
          this.notifyRefresh();
        } else {
          this.toast.error(res.message || 'ดำเนินการไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการขอยกเลิกเคสปัญหา');
      }
    });
  }

  submitStatusChange(): void {
    if (!this.statusMessage.trim()) {
      this.toast.error('กรุณากรอกข้อความรายละเอียดการเปลี่ยนสถานะ');
      return;
    }

    // Closing (or legacy awaiting confirm) requires resolution summary
    if ((this.targetStatus === 'closed' || this.targetStatus === 'awaiting_customer_confirm')
      && !this.resolutionSummary.trim()) {
      this.toast.error('การปิดงานจำเป็นต้องกรอกสรุปผลการแก้ไขปัญหาให้กับลูกค้า');
      return;
    }

    const userId = Number(localStorage.getItem('userId') || this.cookieService.get('user_id') || 0);
    const menuId = this.permissionService.getMenuId(this.currentUrl) ?? 0;

    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลสิทธิ์ผู้ใช้งาน');
      return;
    }

    const payload = {
      userId,
      menuId,
      serviceCaseId: this.caseId,
      newStatus: this.targetStatus,
      message: this.statusMessage.trim(),
      resolutionSummary:
        this.targetStatus === 'closed' || this.targetStatus === 'awaiting_customer_confirm'
          ? this.resolutionSummary.trim()
          : undefined,
    };

    this.isSubmittingStatus = true;
    this.apiCase.updateServiceCaseStatus(payload).subscribe({
      next: (res) => {
        this.isSubmittingStatus = false;
        if (res.isSuccess) {
          this.toast.success(res.message || 'เปลี่ยนสถานะเรียบร้อย');
          this.showStatusDialog = false;
          this.loadCaseDetail();
          this.notifyRefresh();
        } else {
          this.toast.error(res.message || 'ดำเนินการไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSubmittingStatus = false;
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาดในการอัปเดตสถานะ');
      }
    });
  }

  get canShowActivityHistory(): boolean {
    return this.caseId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
